'use client';

import { useState, useCallback } from 'react';
import Link from 'next/link';
import { LocaleLink } from '@/core/i18n';
import { ArrowRight, Clock, User, BookOpen } from 'lucide-react';
import { cn } from '@/core/utils/cn';
import { BLOG_POSTS, BLOG_CATEGORIES } from './blog.data';
import type { BlogCategory, BlogPostData } from './blog.data';
import { usePageContent } from '@/core/i18n/content';
import { BLOG_CONTENT } from '@/core/i18n/content/blog.content';
import { BLOG_POST_ARTICLES } from '@/core/i18n/content/blog-post-articles.content';
import type { BlogContent } from '@/core/i18n/content/blog.types';
import { useTranslation } from '@/core/i18n';

/* -------------------------------------------------------------------------- */
/*  Category colour map                                                       */
/* -------------------------------------------------------------------------- */

const CATEGORY_COLORS: Record<BlogCategory, { bg: string; text: string }> = {
  guides: { bg: 'bg-hms-teal/10', text: 'text-hms-teal' },
  industry: { bg: 'bg-hms-blue/10', text: 'text-hms-blue' },
  product: { bg: 'bg-hms-gold/10', text: 'text-hms-gold' },
  trends: { bg: 'bg-hms-green/10', text: 'text-hms-green' },
};

/* -------------------------------------------------------------------------- */
/*  BlogHero                                                                  */
/* -------------------------------------------------------------------------- */

function BlogHero({ c }: { c: BlogContent }) {
  const { t } = useTranslation();
  return (
    <section
      id="blog-hero"
      className="relative w-full overflow-hidden bg-hms-bg"
    >
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse 80% 60% at 50% 30%, rgba(200,145,58,0.08) 0%, transparent 70%)',
        }}
      />
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse 60% 50% at 50% 80%, rgba(13,158,153,0.04) 0%, transparent 70%)',
        }}
      />

      <div className="relative mx-auto max-w-4xl px-4 pb-12 pt-24 text-center sm:px-6 sm:pt-32 lg:px-8 lg:pt-36">
        <div className="mb-4 flex items-center justify-center gap-2">
          <BookOpen className="h-5 w-5 text-hms-gold" />
          <span className="text-xs font-semibold uppercase tracking-widest text-hms-gold">
            {c.hero.badge}
          </span>
        </div>
        <h1
          id="blog-hero-title"
          className={cn(
            'font-poppins text-4xl font-extrabold leading-tight tracking-tight sm:text-5xl lg:text-6xl',
            'bg-gradient-to-r from-hms-gold via-hms-goldLt to-amber-400 bg-clip-text text-transparent',
          )}
        >
          {c.hero.title}
        </h1>
        <p
          id="blog-hero-subtitle"
          className="mx-auto mt-5 max-w-xl text-base text-hms-textSub sm:text-lg"
        >
          {c.hero.subtitle}
        </p>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  BlogCategoryFilter                                                        */
/* -------------------------------------------------------------------------- */

function BlogCategoryFilter({
  active,
  onSelect,
  c,
}: {
  active: BlogCategory | 'all';
  onSelect: (cat: BlogCategory | 'all') => void;
  c: BlogContent;
}) {
  const FILTER_LABELS: Record<BlogCategory | 'all', string> = {
    all: c.filters.all,
    guides: c.filters.guides,
    industry: c.filters.industry,
    product: c.filters.product,
    trends: c.filters.trends,
  };
  const categories: Array<{ key: BlogCategory | 'all'; label: string }> = [
    { key: 'all', label: FILTER_LABELS.all },
    ...Object.keys(BLOG_CATEGORIES).map((key) => ({
      key: key as BlogCategory,
      label: FILTER_LABELS[key as BlogCategory],
    })),
  ];

  return (
    <div id="blog-category-filters" className="flex flex-wrap items-center justify-center gap-2">
      {categories.map(({ key, label }) => {
        const isActive = active === key;
        return (
          <button
            key={key}
            id={`blog-filter-${key}`}
            type="button"
            onClick={() => onSelect(key)}
            className={cn(
              'rounded-lg px-4 py-2 text-sm font-medium transition-all duration-200',
              isActive
                ? 'bg-hms-gold text-hms-bg shadow-md shadow-hms-gold/20'
                : 'border border-hms-border bg-hms-card text-hms-textSub hover:border-hms-gold/30 hover:text-hms-gold',
            )}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  BlogCard                                                                  */
/* -------------------------------------------------------------------------- */

/** Helper to get translated post for current locale, with English fallback */
function getTranslatedPost(post: BlogPostData, locale: string): BlogPostData {
  const localeArticles = BLOG_POST_ARTICLES[locale as keyof typeof BLOG_POST_ARTICLES] || BLOG_POST_ARTICLES['en'] || {};
  const tp = localeArticles[post.slug];
  return tp ? {
    ...post,
    title: tp.title,
    description: tp.description,
    excerpt: tp.excerpt,
    author: { ...post.author, name: tp.authorName, role: tp.authorRole },
    sections: tp.sections,
  } : post;
}

function BlogCard({ post, index, c, locale }: { post: BlogPostData; index: number; c: BlogContent; locale: string }) {
  const translatedPost = getTranslatedPost(post, locale);
  const categoryColor = CATEGORY_COLORS[post.category];
  const { t } = useTranslation();

  // Use locale-aware date formatting
  const localeMap: Record<string, string> = {
    en: 'en-IN', ar: 'ar-SA', th: 'th-TH', es: 'es-ES', fr: 'fr-FR',
    de: 'de-DE', ja: 'ja-JP', zh: 'zh-CN', hi: 'hi-IN',
  };
  const dateLocale = localeMap[locale] || 'en-IN';
  const formattedDate = new Date(post.publishedAt).toLocaleDateString(dateLocale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

  // Translate readTime: "8 min read" → use card.readTimeUnit
  const readTimeMatch = post.readTime.match(/^(\d+)\s*(min|minute)s?\s+(read)$/i);
  let readTimeDisplay = post.readTime;
  if (readTimeMatch) {
    const count = readTimeMatch[1];
    readTimeDisplay = `${count} ${t('blogPost.card.readTimeUnit')}`;
  }

  return (
    <article
      id={`blog-card-${index}`}
      className={cn(
        'group relative flex flex-col rounded-xl border border-hms-border bg-hms-card',
        'transition-all duration-300',
        'hover:border-hms-gold/25 hover:-translate-y-1 hover:shadow-lg hover:shadow-black/20',
      )}
    >
      {/* Content */}
      <div className="flex flex-1 flex-col p-6">
        {/* Category badge */}
        <span
          id={`blog-card-category-${index}`}
          className={cn(
            'mb-4 inline-flex w-fit items-center rounded-full px-3 py-1',
            'text-[11px] font-semibold uppercase tracking-wider',
            categoryColor.bg,
            categoryColor.text,
          )}
        >
          {c.categories[post.category]}
        </span>

        {/* Title */}
        <LocaleLink
          id={`blog-card-link-${index}`}
          href={`/blog/${post.slug}`}
          className="group/link"
        >
          <h3
            className={cn(
              'text-lg font-bold leading-snug tracking-tight text-hms-text',
              'transition-colors duration-200 group-hover/link:text-hms-gold',
            )}
          >
            {translatedPost.title}
          </h3>
        </LocaleLink>

        {/* Excerpt */}
        <p className="mt-3 flex-1 text-[13px] leading-relaxed text-hms-textSub line-clamp-3">
          {translatedPost.excerpt}
        </p>

        {/* Footer */}
        <div className="mt-5 flex items-center gap-3 border-t border-hms-border/50 pt-4">
          {/* Author avatar */}
          <div
            className={cn(
              'flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
              'bg-hms-gold/10 text-[11px] font-bold text-hms-gold',
            )}
          >
            {post.author.initials}
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-[13px] font-medium text-hms-text">
              {translatedPost.author.name}
            </p>
            <div className="flex items-center gap-3 text-[11px] text-hms-textDim">
              <span className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {post.readTime}
              </span>
              <span>{formattedDate}</span>
            </div>
          </div>
        </div>
      </div>
    </article>
  );
}

/* -------------------------------------------------------------------------- */
/*  BlogCTA                                                                   */
/* -------------------------------------------------------------------------- */

function BlogCTA({ c }: { c: BlogContent }) {
  return (
    <section
      id="blog-index-cta"
      className="relative w-full overflow-hidden bg-hms-bg py-20 sm:py-28"
    >
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse 70% 50% at 50% 50%, rgba(200,145,58,0.06) 0%, transparent 70%)',
        }}
      />
      <div className="relative mx-auto max-w-3xl px-4 text-center sm:px-6 lg:px-8">
        <h2
          id="blog-index-cta-title"
          className={cn(
            'text-3xl font-extrabold tracking-tight sm:text-4xl',
            'bg-gradient-to-r from-hms-gold via-hms-goldLt to-amber-400',
            'bg-clip-text text-transparent',
          )}
        >
          {c.cta.title}
        </h2>
        <p
          id="blog-index-cta-subtitle"
          className="mx-auto mt-5 max-w-xl text-base text-hms-textSub sm:text-lg"
        >
          {c.cta.subtitle}
        </p>
        <div className="mt-10 flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
          <LocaleLink
            id="blog-index-cta-trial-btn"
            href="/register-hotel"
            className={cn(
              'inline-flex items-center gap-2 rounded-lg bg-hms-gold px-8 py-3.5',
              'text-sm font-semibold tracking-wide text-hms-bg',
              'shadow-lg shadow-hms-gold/20 transition-all duration-200',
              'hover:bg-hms-goldLt hover:shadow-hms-gold/30 active:scale-[0.98]',
              'w-full sm:w-auto justify-center',
            )}
          >
            {c.cta.trialBtn}
            <ArrowRight className="h-4 w-4" />
          </LocaleLink>
          <LocaleLink
            id="blog-index-cta-contact-btn"
            href="/contact"
            className={cn(
              'inline-flex items-center justify-center rounded-lg border border-hms-border px-8 py-3.5',
              'text-sm font-semibold tracking-wide text-hms-text',
              'transition-all duration-200 hover:border-hms-gold/40 hover:text-hms-gold',
              'w-full sm:w-auto',
            )}
          >
            {c.cta.contactBtn}
          </LocaleLink>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  BlogIndex — Main Export                                                   */
/* -------------------------------------------------------------------------- */

export function BlogIndex() {
  const c = usePageContent(BLOG_CONTENT);
  const { locale } = useTranslation();
  const [activeCategory, setActiveCategory] = useState<BlogCategory | 'all'>('all');

  const handleSelect = useCallback((cat: BlogCategory | 'all') => {
    setActiveCategory(cat);
  }, []);

  const filtered =
    activeCategory === 'all'
      ? BLOG_POSTS
      : BLOG_POSTS.filter((p) => p.category === activeCategory);

  return (
    <main id="blog-index-page">
      <BlogHero c={c} />

      <section id="blog-posts-section" className="w-full bg-hms-bg py-16 sm:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <BlogCategoryFilter active={activeCategory} onSelect={handleSelect} c={c} />

          {filtered.length > 0 ? (
            <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {filtered.map((post, idx) => (
                <BlogCard key={post.slug} post={post} index={idx} c={c} locale={locale} />
              ))}
            </div>
          ) : (
            <div
              id="blog-empty-state"
              className="mt-16 flex flex-col items-center justify-center text-center"
            >
              <BookOpen className="h-12 w-12 text-hms-textDim" />
              <p className="mt-4 text-sm font-medium text-hms-textSub">
                {c.empty}
              </p>
            </div>
          )}
        </div>
      </section>

      <BlogCTA c={c} />
    </main>
  );
}
